import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import EmptyState from "../../components/Common/EmptyState"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import ListingTabs from "../../components/Listing/ListingTabs"
import SearchInput from "../../components/Listing/SearchInput"
import Pagination from "../../components/Listing/Pagination"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import {
  DEFAULT_NOTIFICATION_LIST_TAB,
  fetchNotificationCount,
  getNotificationListTab,
  mapNotification,
  markAllNotificationsRead,
  markNotificationsReadByIds,
  NOTIFICATION_LIST_TABS,
  openNotification,
  refreshNotificationInbox,
} from "../../helpers/notifications"
import {
  clearUnreadInbox,
  markNotificationRead,
} from "../../store/slices/notificationsSlice"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const PAGE_SIZE = 13
const SKELETON_ROWS = 8

/**
 * Member Notifications listing — Unread / Read tabs (`is_read=0|1`).
 */
const Notifications = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const pulse = useSelector(state => state.notifications?.pulse || 0)
  const [activeTab, setActiveTab] = useState(DEFAULT_NOTIFICATION_LIST_TAB)
  const [items, setItems] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const tabConfig = useMemo(
    () => getNotificationListTab(activeTab),
    [activeTab],
  )

  const tabItems = useMemo(
    () =>
      NOTIFICATION_LIST_TABS.map(tab => ({
        key: tab.key,
        label: tab.label,
        ...(tab.key === "unread" && unreadCount > 0
          ? { count: unreadCount, countTone: "awaiting" }
          : {}),
      })),
    [unreadCount],
  )

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const refreshCount = useCallback(async () => {
    const counts = await fetchNotificationCount()
    setUnreadCount(counts.unread)
  }, [])

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        per_page: PAGE_SIZE,
        is_read: tabConfig.isRead,
      }
      if (debouncedSearch) {
        params.search = debouncedSearch
      }

      const [response] = await Promise.all([
        get(endpoints.getNotifications, { params }),
        refreshCount(),
      ])

      if (response?.data?.success) {
        const result = response.data.result || {}
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : []
        const rows = list.map(mapNotification)
        const total = Number(result.total)
        const perPage =
          Number(result.per_page || result.page_size) || PAGE_SIZE

        setItems(rows)
        setTotalItems(Number.isFinite(total) ? total : rows.length)
        setPageSize(perPage)

        const lastPage = Number(result.last_page)
        const totalPages = Math.max(
          1,
          Number.isFinite(lastPage) && lastPage > 0
            ? lastPage
            : Math.ceil(
                (Number.isFinite(total) ? total : rows.length) / perPage,
              ) || 1,
        )
        if (currentPage > totalPages) {
          setCurrentPage(totalPages)
        }
      } else if (response) {
        toastApiError(response, "Failed to load notifications")
        setItems([])
        setTotalItems(0)
      }
    } catch (err) {
      toastApiError(err, "Failed to load notifications")
      setItems([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, debouncedSearch, refreshCount, tabConfig.isRead])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  useEffect(() => {
    if (pulse < 1) return undefined
    fetchNotifications()
    return undefined
  }, [pulse, fetchNotifications])

  const handleTabChange = nextTab => {
    if (nextTab === activeTab) return
    setActiveTab(nextTab)
    setCurrentPage(1)
  }

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return

    const response = await markAllNotificationsRead()
    if (!response?.data?.success) {
      if (response) {
        toastApiError(response, "Failed to mark notifications as read")
      }
      return
    }

    toast.success(
      response.data.message || "All notifications marked as read.",
      TOAST_OPTIONS,
    )
    setUnreadCount(0)
    dispatch(clearUnreadInbox())

    if (activeTab === "unread") {
      setItems([])
      setTotalItems(0)
    } else {
      fetchNotifications()
    }
  }

  const handleItemClick = async item => {
    if (!item) return

    openNotification(item, navigate)

    if (item.read) return

    const response = await markNotificationsReadByIds([item.id])
    if (response?.data?.success) {
      dispatch(markNotificationRead(item.id))
      await refreshCount()
      refreshNotificationInbox(dispatch)

      if (activeTab === "unread") {
        setItems(prev => prev.filter(row => row.id !== item.id))
        setTotalItems(prev => Math.max(0, prev - 1))
      } else {
        setItems(prev =>
          prev.map(row => (row.id === item.id ? { ...row, read: true } : row)),
        )
      }
    } else if (response) {
      toastApiError(response, "Failed to mark notification as read")
    }
  }

  const emptyTitle =
    activeTab === "unread"
      ? "No unread notifications"
      : "No read notifications"

  return (
    <div className="page-content">
      <div className="notifications">
        <PageHeader
          className="notifications__header"
          title="Notifications"
          subtitle="Stay on top of mentions, invites, and updates."
          actions={
            <div className="notifications__actions">
              <SearchInput
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Search by Content..."
                iconSrc={ICONS.search}
                className="notifications__search"
              />
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                trailingIconSrc={ICONS.tickDoubleWhite}
                trailingIconAlt=""
                disabled={unreadCount === 0}
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </CustomButton>
            </div>
          }
        />

        <ListingTabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
        />

        <div
          className="notifications__list"
          role="list"
          aria-busy={isLoading || undefined}
        >
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="notifications__row notifications__row--skeleton"
                  aria-hidden="true"
                >
                  <span className="notifications__dot notifications__dot--skeleton" />
                  <span className="notifications__content">
                    <span className="notifications__skeleton-title amok-skeleton" />
                    <span className="notifications__skeleton-time amok-skeleton" />
                  </span>
                </div>
              ))
            : items.length === 0 ? (
                <EmptyState title={emptyTitle} />
              ) : (
                items.map(notification => (
                  <button
                    key={notification.id}
                    type="button"
                    role="listitem"
                    className={`notifications__row${
                      !notification.read ? " notifications__row--unread" : ""
                    }`}
                    onClick={() => handleItemClick(notification)}
                  >
                    <span className="notifications__dot" aria-hidden="true" />
                    <span className="notifications__content">
                      <span className="notifications__title">
                        {notification.title}
                      </span>
                      <span className="notifications__time">
                        {notification.time}
                      </span>
                    </span>
                  </button>
                ))
              )}
        </div>

        <Pagination
          className="notifications__pagination"
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel="Notifications"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      </div>
    </div>
  )
}

export default Notifications
